import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import getProfileSetup from "../apis/getProfileSetup";
import {
  extractOpportunityCountFromSetup,
  isSuccessfulSetupStatus,
  isTerminalSetupStatus,
  mapSetupStateToSteps,
} from "../utils/profileSetupProgress";

const INITIAL_POLL_DELAY_MS = 1000;
const POLL_INTERVAL_MS = 10000;

const ProfileSetupContext = createContext(null);

export function ProfileSetupProvider({ children }) {
  const [steps, setSteps] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [opportunityCount, setOpportunityCount] = useState(null);
  const hasStartedRef = useRef(false);
  const mountedRef = useRef(true);
  const pollingActiveRef = useRef(false);
  const initialTimerRef = useRef(null);
  const intervalRef = useRef(null);

  const stopPolling = useCallback(() => {
    pollingActiveRef.current = false;

    if (initialTimerRef.current) {
      window.clearTimeout(initialTimerRef.current);
      initialTimerRef.current = null;
    }

    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const handleSetupState = useCallback(
    (state) => {
      if (!mountedRef.current || !state) return;

      setSteps(mapSetupStateToSteps(state));
      setIsLoading(false);

      if (!isTerminalSetupStatus(state.status)) return;

      stopPolling();

      if (state.status === "failed") {
        setError("Profile setup failed. Please refresh the page or try again later.");
        return;
      }

      setOpportunityCount(extractOpportunityCountFromSetup(state));
      setIsComplete(true);
    },
    [stopPolling]
  );

  const pollOnce = useCallback(async () => {
    if (!mountedRef.current || !pollingActiveRef.current) return;

    try {
      const state = await getProfileSetup();
      handleSetupState(state);
    } catch (pollError) {
      if (!mountedRef.current || !pollingActiveRef.current) return;

      const status = pollError?.response?.status;
      if (status === 404) {
        return;
      }

      stopPolling();
      setError(pollError?.message || "Could not load setup progress");
      setIsLoading(false);
    }
  }, [handleSetupState, stopPolling]);

  const startSetup = useCallback(() => {
    if (hasStartedRef.current || isComplete) return;

    hasStartedRef.current = true;
    pollingActiveRef.current = true;
    setIsLoading(true);
    setError(null);

    initialTimerRef.current = window.setTimeout(() => {
      if (!mountedRef.current || !pollingActiveRef.current) return;

      pollOnce();
      intervalRef.current = window.setInterval(() => {
        pollOnce();
      }, POLL_INTERVAL_MS);
    }, INITIAL_POLL_DELAY_MS);
  }, [isComplete, pollOnce]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  const hasActiveSteps = steps.some(
    (step) => step.status === "active" || step.status === "done"
  );

  const value = useMemo(
    () => ({
      steps,
      isComplete,
      isLoading,
      hasActiveSteps,
      error,
      opportunityCount,
      startSetup,
    }),
    [steps, isComplete, isLoading, hasActiveSteps, error, opportunityCount, startSetup]
  );

  return <ProfileSetupContext.Provider value={value}>{children}</ProfileSetupContext.Provider>;
}

export function useProfileSetup() {
  const context = useContext(ProfileSetupContext);
  if (!context) {
    throw new Error("useProfileSetup must be used within ProfileSetupProvider");
  }
  return context;
}
